import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import type {
  FittingKind,
  PipeDiameterMm,
  PlanObjectPoint,
} from "@sts/rules-data";
import { formatLength } from "@sts/scan-viewer";
import type { PlanObject } from "../api/types.js";
import type { ObjectFit } from "./plan-fit.js";
import { toView, toViewDirection, viewTurnDeg, type PlanFit } from "./plan-view.js";
import { HIT_UNITS, round, uprightAngle } from "./plan-marks.jsx";
import {
  axesOf,
  footprintM,
  heatPumpCaption,
  objectCaption,
  placedCorners,
  radiatorCaption,
  runLengthM,
} from "./plan-objects.js";

/**
 * How the plan's objects are drawn — the outdoor unit, radiators, pipe runs and
 * fittings — each a pure function of plan metres and a `PlanFit`, like the
 * marks in `plan-marks.tsx`.
 *
 * TRUE SCALE WHERE THERE IS A SIZE. An outdoor unit is its casing's width by
 * depth and a radiator its length by depth, drawn through the same `toView` as
 * every wall, so a 1100 mm unit measures 1100 mm against the plan's own scale
 * bar. Pipe line weights and fitting symbols are drawing conventions — a 15 mm
 * pipe would be a fraction of a pixel — and never feed a number.
 *
 * Colour is CSS over the portal's tokens (`.floor-plan__obj*` in styles.css);
 * geometry and pointer behaviour are set here, inline.
 */

const INERT = { pointerEvents: "none" } as const;

/** Line weight per pipe size, px. A convention: bigger pipe, heavier line. */
const PIPE_WEIGHT: Readonly<Record<PipeDiameterMm, number>> = { 10: 1.75, 15: 2.5, 22: 3.5, 28: 4.5, 35: 5.5 };

export type ObjectPointerDown = (
  id: string,
  event: ReactPointerEvent<SVGElement>,
  vertexIndex?: number,
) => void;

interface MarkProps {
  readonly object: PlanObject;
  readonly fit: PlanFit;
  readonly selected?: boolean;
  /** Drawn to show where a click would put it, not a saved object. */
  readonly ghost?: boolean;
  /** Takes pointer input: select, drag. False while a tool is in hand. */
  readonly interactive?: boolean;
  readonly onPointerDown?: ObjectPointerDown;
  /** For a radiator: which way (plan unit vector) its label goes — into the room. */
  readonly labelToward?: PlanObjectPoint;
  /** Its fit (`plan-fit.ts`): problems draw it red, an unchecked sill amber. */
  readonly fitCheck?: ObjectFit;
  /**
   * For a cylinder or buffer: where its name's centre goes, px below (+) or
   * above (−) the vessel's centre — `vesselNameOffsets`, which keeps two
   * neighbouring vessels' names apart. Default: just under the circle.
   */
  readonly nameDy?: number;
}

function classes(
  object: PlanObject,
  selected: boolean,
  ghost: boolean,
  saving: boolean,
  fitCheck: ObjectFit | undefined,
): string {
  const problem = (fitCheck?.problems.length ?? 0) > 0;
  const unknown = !problem && (fitCheck?.notes.some((n) => n.kind === "sill-unknown") ?? false);
  return [
    "floor-plan__obj",
    `floor-plan__obj--${object.kind}`,
    object.kind === "pipe" ? `floor-plan__obj--${object.spec.circuit}` : "",
    selected ? "is-selected" : "",
    ghost ? "is-ghost" : "",
    saving ? "is-saving" : "",
    problem ? "has-fit-problem" : "",
    unknown ? "has-fit-unknown" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/** What a pointer resting on the object says: its caption, then every problem and unchecked sill. */
function tooltip(caption: string, fitCheck: ObjectFit | undefined): string {
  const lines = [
    caption,
    ...(fitCheck?.problems.map((p) => p.message) ?? []),
    ...(fitCheck?.notes.filter((n) => n.kind === "sill-unknown").map((n) => n.message) ?? []),
  ];
  return lines.join("\n");
}

function pathOf(points: readonly PlanObjectPoint[], fit: PlanFit, close = false): string {
  const d = points
    .map((p, i) => {
      const v = toView(p, fit);
      return `${i === 0 ? "M" : "L"} ${round(v.x)},${round(v.y)}`;
    })
    .join(" ");
  return close ? `${d} Z` : d;
}

/** A vessel's name, drawn centred on its point: 10 px bold (`.floor-plan__obj-label--small`). */
const VESSEL_NAME_PX = 10;
/** Half the name's height, and a pixel of air each side. */
const VESSEL_NAME_HALF_HEIGHT = 7;

function vesselName(object: PlanObject): string {
  if (object.kind !== "cylinder" && object.kind !== "buffer") return "";
  return `${object.label ? `${object.label} · ` : ""}${object.spec.name}${object.spec.genericId !== null ? " (nominal)" : ""}`;
}

function vesselNameDefaultDy(radiusPx: number): number {
  return Math.max(radiusPx, 3) + 12;
}

/**
 * Where each cylinder's and buffer's name goes on screen, as `nameDy`: under
 * the circle, or over it, or a line further out either way — the first of
 * those clear of every vessel name already placed and of every other vessel's
 * circle. The same rule the printed plan follows (`plan-svg.ts`, 37950be): a
 * cylinder and a buffer side by side in a cupboard would otherwise print one
 * name over the other. Vessels are taken in the order given, so pass the
 * storey's objects in drawing order (and a ghost last). Width is estimated at
 * 0.62 em a character — generous for a bold UI face — since jsdom cannot
 * measure text.
 */
export function vesselNameOffsets(objects: readonly PlanObject[], fit: PlanFit): Map<string, number> {
  const circles: { id: string; c: { x: number; y: number }; r: number; name: string }[] = [];
  for (const o of objects) {
    if (o.kind !== "cylinder" && o.kind !== "buffer") continue;
    circles.push({
      id: o.id,
      c: toView({ x: o.geometry.x, y: o.geometry.y }, fit),
      r: Math.max((o.spec.diameterMm / 2000) * fit.pixelsPerMetre, 3),
      name: vesselName(o),
    });
  }
  const placed: { x0: number; x1: number; y0: number; y1: number }[] = [];
  const out = new Map<string, number>();
  circles.forEach(({ id, c, r, name }, i) => {
    const half = (name.length * VESSEL_NAME_PX * 0.62) / 2;
    const box = (dy: number) => ({ x0: c.x - half, x1: c.x + half, y0: c.y + dy - VESSEL_NAME_HALF_HEIGHT, y1: c.y + dy + VESSEL_NAME_HALF_HEIGHT });
    const clearOfNames = (b: ReturnType<typeof box>) => placed.every((n) => b.x1 <= n.x0 || n.x1 <= b.x0 || b.y1 <= n.y0 || n.y1 <= b.y0);
    const clearOfVessels = (b: ReturnType<typeof box>) =>
      circles.every((other, j) => {
        if (j === i) return true;
        const nx = Math.max(b.x0, Math.min(other.c.x, b.x1));
        const ny = Math.max(b.y0, Math.min(other.c.y, b.y1));
        return Math.hypot(nx - other.c.x, ny - other.c.y) >= other.r;
      });
    const near = vesselNameDefaultDy(r);
    const tries = [near, -near, near + 14, -(near + 14), near + 28, -(near + 28)];
    const dy = tries.find((t) => clearOfNames(box(t)) && clearOfVessels(box(t))) ?? tries.find((t) => clearOfNames(box(t))) ?? near;
    placed.push(box(dy));
    out.set(id, dy);
  });
  return out;
}

export function PlanObjectMark({
  object,
  fit,
  selected = false,
  ghost = false,
  interactive = false,
  onPointerDown,
  labelToward,
  fitCheck,
  nameDy,
}: MarkProps): ReactNode {
  const saving = object.id.startsWith("provisional-");
  const live = interactive && !ghost && !saving;
  const down =
    live && onPointerDown
      ? (event: ReactPointerEvent<SVGElement>) => onPointerDown(object.id, event)
      : undefined;
  const common = {
    className: classes(object, selected, ghost, saving, fitCheck),
    "data-object-id": ghost ? undefined : object.id,
    "data-object-kind": object.kind,
    style: live ? { cursor: "move" } : INERT,
  };

  if (object.kind === "pipe") {
    const d = pathOf(object.geometry.points, fit);
    const weight = PIPE_WEIGHT[object.spec.diameterMm];
    const pts = object.geometry.points;
    return (
      <g {...common} aria-label={objectCaption(object)}>
        <path
          className="floor-plan__pipe"
          d={d}
          fill="none"
          strokeWidth={weight}
          strokeLinejoin="round"
          strokeLinecap="round"
          strokeDasharray={object.spec.circuit === "return" ? `${weight * 3} ${weight * 2}` : undefined}
          style={INERT}
        />
        {live ? (
          <path
            className="floor-plan__hit"
            d={d}
            fill="none"
            stroke="transparent"
            strokeWidth={HIT_UNITS}
            onPointerDown={down}
          />
        ) : null}
        {selected && live
          ? pts.map((p, i) => {
              const v = toView(p, fit);
              return (
                <circle
                  key={i}
                  className="floor-plan__handle"
                  data-testid="pipe-handle"
                  cx={round(v.x)}
                  cy={round(v.y)}
                  r={5.5}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                    onPointerDown?.(object.id, event, i);
                  }}
                />
              );
            })
          : null}
        {selected ? <RunLengthTag points={pts} fit={fit} /> : null}
      </g>
    );
  }

  if (object.kind === "fitting") {
    const c = toView({ x: object.geometry.x, y: object.geometry.y }, fit);
    return (
      <g {...common} aria-label={objectCaption(object)}>
        <g transform={`translate(${round(c.x)},${round(c.y)}) rotate(${round(object.geometry.rotationDeg + viewTurnDeg(fit))})`}>
          <FittingSymbol fitting={object.spec.fitting} />
        </g>
        {live ? (
          <circle
            className="floor-plan__hit"
            cx={round(c.x)}
            cy={round(c.y)}
            r={11}
            fill="transparent"
            onPointerDown={down}
          />
        ) : null}
      </g>
    );
  }

  // Cylinder and buffer: a true-size circle, its tag inside, its name beside it.
  if (object.kind === "cylinder" || object.kind === "buffer") {
    const c = toView({ x: object.geometry.x, y: object.geometry.y }, fit);
    const r = (object.spec.diameterMm / 2000) * fit.pixelsPerMetre;
    return (
      <g {...common} aria-label={objectCaption(object)}>
        <circle
          className="floor-plan__obj-body floor-plan__obj-vessel"
          cx={round(c.x)}
          cy={round(c.y)}
          r={round(Math.max(r, 3))}
          onPointerDown={down}
          data-testid="plan-vessel"
        />
        {live ? (
          <circle className="floor-plan__hit" cx={round(c.x)} cy={round(c.y)} r={Math.max(r, 11)} fill="transparent" onPointerDown={down} />
        ) : null}
        {r > 8 ? (
          <text className="floor-plan__obj-label floor-plan__obj-label--small" x={round(c.x)} y={round(c.y + 3)} style={INERT}>
            {object.kind === "cylinder" ? "CYL" : "BUF"}
          </text>
        ) : null}
        <text
          className="floor-plan__obj-label floor-plan__obj-label--small"
          x={round(c.x)}
          y={round(c.y + (nameDy ?? vesselNameDefaultDy(r)))}
          style={INERT}
        >
          {object.label ? `${object.label} · ` : ""}
          {object.spec.name}
          {object.spec.genericId !== null ? " (nominal)" : ""}
        </text>
      </g>
    );
  }

  // Heat pump and radiator: a true-size rectangle.
  const size = footprintM(object);
  if (!size) return null;
  // Hovering a flagged object says what is wrong with it.
  const flagged =
    fitCheck && (fitCheck.problems.length > 0 || fitCheck.notes.some((n) => n.kind === "sill-unknown"));
  const title = flagged ? <title>{tooltip(objectCaption(object), fitCheck)}</title> : null;
  const corners = placedCorners(object.geometry, size.lengthM, size.depthM);
  const outline = pathOf(corners, fit, true);
  // u and v in plan metres (for points on the object), and the same two
  // directions on the drawing (for labels and hit areas beside it) — the
  // drawing may be turned (PlanFit.rotationDeg).
  const { u, v } = axesOf(object.geometry.rotationDeg);
  const uView = toViewDirection(u, fit);
  const vView = toViewDirection(v, fit);
  const centre = toView({ x: object.geometry.x, y: object.geometry.y }, fit);
  const halfDepthPx = (size.depthM / 2) * fit.pixelsPerMetre;
  const angle = uprightAngle(uView);

  if (object.kind === "heat-pump") {
    // The front face (+v), where the fan blows, drawn heavier; a fan disc on top.
    const front = pathOf([corners[2] as PlanObjectPoint, corners[3] as PlanObjectPoint], fit);
    const fanR = Math.min(size.lengthM, size.depthM) * 0.36 * fit.pixelsPerMetre;
    const fanAt = {
      x: object.geometry.x + u.x * size.lengthM * 0.18,
      y: object.geometry.y + u.y * size.lengthM * 0.18,
    };
    const fan = toView(fanAt, fit);
    const labelAt = {
      x: centre.x + vView.x * (halfDepthPx + 12),
      y: centre.y + vView.y * (halfDepthPx + 12),
    };
    return (
      <g {...common} aria-label={heatPumpCaption(object.spec)}>
        {title}
        <path className="floor-plan__obj-body" d={outline} onPointerDown={down} />
        <path className="floor-plan__obj-front" d={front} fill="none" style={INERT} />
        {fanR > 3 ? (
          <circle className="floor-plan__obj-fan" cx={round(fan.x)} cy={round(fan.y)} r={round(fanR)} style={INERT} />
        ) : null}
        <text
          className="floor-plan__obj-label"
          x={round(labelAt.x)}
          y={round(labelAt.y)}
          transform={`rotate(${round(angle)},${round(labelAt.x)},${round(labelAt.y)})`}
          style={INERT}
        >
          {object.label ? `${object.label} · ` : ""}
          {heatPumpCaption(object.spec)}
        </text>
      </g>
    );
  }

  // Radiator: the body, a line along it for the panels, and its size beside it.
  const toward = labelToward ?? v;
  const sign = toward.x * v.x + toward.y * v.y >= 0 ? 1 : -1;
  const labelAt = {
    x: centre.x + vView.x * sign * (halfDepthPx + 9),
    y: centre.y + vView.y * sign * (halfDepthPx + 9),
  };
  const inset = (fraction: number): string => {
    const a = {
      x: object.geometry.x - (u.x * size.lengthM) / 2 + v.x * size.depthM * fraction,
      y: object.geometry.y - (u.y * size.lengthM) / 2 + v.y * size.depthM * fraction,
    };
    const b = { x: a.x + u.x * size.lengthM, y: a.y + u.y * size.lengthM };
    return pathOf([a, b], fit);
  };
  return (
    <g {...common} aria-label={radiatorCaption(object.spec)}>
      {title}
      {/* The hit area is never thinner than a finger: a 78 mm K1 is a few pixels deep. */}
      {live ? (
        <path
          className="floor-plan__hit"
          d={pathOf([corners[0] as PlanObjectPoint, corners[1] as PlanObjectPoint], fit)}
          transform={`translate(${round(vView.x * halfDepthPx)},${round(vView.y * halfDepthPx)})`}
          stroke="transparent"
          strokeWidth={Math.max(HIT_UNITS, halfDepthPx * 2)}
          fill="none"
          onPointerDown={down}
        />
      ) : null}
      <path className="floor-plan__obj-body" d={outline} style={INERT} />
      {halfDepthPx > 2.5 ? <path className="floor-plan__obj-panel" d={inset(0.5)} fill="none" style={INERT} /> : null}
      <text
        className="floor-plan__obj-label floor-plan__obj-label--small"
        x={round(labelAt.x)}
        y={round(labelAt.y)}
        transform={`rotate(${round(angle)},${round(labelAt.x)},${round(labelAt.y)})`}
        style={INERT}
      >
        {object.label ? `${object.label} · ` : ""}
        {radiatorCaption(object.spec)}
      </text>
    </g>
  );
}

/**
 * A flagged object's warning: a red "!" at one end of it, and — while it is
 * selected, or is the ghost about to be put down — a tag saying what is wrong
 * in a few words. Drawn in a layer above every object so a neighbour cannot
 * hide it. Inert: it never takes the click meant for the object under it.
 */
export function FitFlag({
  object,
  fit,
  fitCheck,
  showTag,
  toward,
}: {
  readonly object: PlanObject;
  readonly fit: PlanFit;
  readonly fitCheck: ObjectFit;
  readonly showTag: boolean;
  /** For a radiator: the room side, where its label already is; the tag goes beyond it. */
  readonly toward?: PlanObjectPoint;
}): ReactNode {
  const size = footprintM(object);
  const first = fitCheck.problems[0];
  if (!size || !first || (object.kind !== "radiator" && object.kind !== "heat-pump")) return null;
  const { u, v } = axesOf(object.geometry.rotationDeg);
  const g = object.geometry;
  const end = toView({ x: g.x + (u.x * size.lengthM) / 2, y: g.y + (u.y * size.lengthM) / 2 }, fit);
  const centre = toView({ x: g.x, y: g.y }, fit);
  const halfDepthPx = (size.depthM / 2) * fit.pixelsPerMetre;
  const away = toward ?? v;
  const sign = away.x * v.x + away.y * v.y >= 0 ? 1 : -1;
  const more = fitCheck.problems.length - 1;
  const text = more > 0 ? `${first.short} · +${more} more` : first.short;
  const w = text.length * 6.1 + 16;
  // The tag is always level, so off a wall that runs up the page it has to go
  // a further half its own width out to clear the object's (turned) label.
  const d = toViewDirection({ x: v.x * sign, y: v.y * sign }, fit);
  const reach = halfDepthPx + (object.kind === "radiator" ? 26 : 32) + Math.abs(d.x) * (w / 2 - 9);
  const at = { x: centre.x + d.x * reach, y: centre.y + d.y * reach };
  return (
    <g
      className="floor-plan__fit-flag"
      data-testid="fit-flag"
      data-fit-for={object.id}
      style={INERT}
      aria-hidden="true"
    >
      <g className="floor-plan__fit-marker" transform={`translate(${round(end.x)},${round(end.y)})`}>
        <circle r={7.5} />
        <text y={0.5}>!</text>
      </g>
      {showTag ? (
        <g className="floor-plan__fit-tag" data-testid="fit-tag">
          <rect x={round(at.x - w / 2)} y={round(at.y - 9)} width={round(w)} height={18} rx={9} />
          <text x={round(at.x)} y={round(at.y)}>
            {text}
          </text>
        </g>
      ) : null}
    </g>
  );
}

/** A fitting's plan symbol, in px about its own origin, pipe along +x. */
function FittingSymbol({ fitting }: { readonly fitting: FittingKind }): ReactNode {
  switch (fitting) {
    case "elbow":
      return (
        <>
          <path className="floor-plan__fitting" d="M 9,0 L 0,0 L 0,9" fill="none" />
          <circle className="floor-plan__fitting-dot" r={3} />
        </>
      );
    case "tee":
      return (
        <>
          <path className="floor-plan__fitting" d="M -9,0 L 9,0 M 0,0 L 0,9" fill="none" />
          <circle className="floor-plan__fitting-dot" r={3} />
        </>
      );
    case "coupling":
      return <path className="floor-plan__fitting" d="M -9,0 L 9,0 M -3,-5 L -3,5 M 3,-5 L 3,5" fill="none" />;
    case "isolation-valve":
      return (
        <>
          <path className="floor-plan__fitting floor-plan__fitting--valve" d="M -8,-5 L 8,5 L 8,-5 L -8,5 Z" />
          <path className="floor-plan__fitting" d="M 0,0 L 0,-8 M -4,-8 L 4,-8" fill="none" />
        </>
      );
    case "reducer":
      // A taper, wide end on the larger pipe (−x).
      return <path className="floor-plan__fitting floor-plan__fitting--valve" d="M -8,-6 L 8,-3 L 8,3 L -8,6 Z" />;
  }
}

/** A run's length, in a pill on its longest leg. */
export function RunLengthTag({
  points,
  fit,
  extraM = 0,
  at,
}: {
  readonly points: readonly PlanObjectPoint[];
  readonly fit: PlanFit;
  /** Added to the drawn length — the rubber band not yet committed. */
  readonly extraM?: number;
  /** Where to put it, in plan metres; defaults to the longest leg's middle. */
  readonly at?: PlanObjectPoint;
}): ReactNode {
  if (points.length < 2 && at === undefined) return null;
  let where = at;
  if (!where) {
    let best = 0;
    for (let i = 1; i < points.length; i += 1) {
      const a = points[i - 1] as PlanObjectPoint;
      const b = points[i] as PlanObjectPoint;
      const l = Math.hypot(b.x - a.x, b.y - a.y);
      if (l >= best) {
        best = l;
        where = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      }
    }
  }
  if (!where) return null;
  const p = toView(where, fit);
  const text = formatLength(runLengthM(points) + extraM, "m");
  const w = Math.max(44, text.length * 7 + 14);
  return (
    <g className="floor-plan__run-length" style={INERT} data-testid="run-length">
      <rect x={round(p.x - w / 2)} y={round(p.y - 22)} width={round(w)} height={18} rx={9} />
      <text x={round(p.x)} y={round(p.y - 13)}>
        {text}
      </text>
    </g>
  );
}
